import { DuctedResultSet } from 'features/ducted-compatibility';
import {
  rankGroupCardSelection,
  rankGroupTailSelections
} from 'utils/ducted-rail-selections';
import { SystemSortOption } from 'utils/system-sort';
import { DuctedCombinationCardProps } from './ducted-combination-card';
import { SystemTypeFacetEntry } from './ducted-facet-merge';
import { featuredRankGroupCard } from './ducted-rail-rank-groups';
import {
  DuctedRailRankGroupCard,
  DuctedRailRankGroupModel,
  DuctedRailSectionModel
} from './ducted-rail-sections-types';
import { DuctedPanelSelection } from './ducted-system-detail-panel';

// The rail's grid for one section, in display order. The client never
// orders ducted rows by a figure of its own: the server pages the pool in
// the "Sort by" order and this module lays that order out.
// - Until "Show more" is clicked (the tail not revealed): every type's lead
//   cards, grouped by type — the kept pairing ahead of the full replacement
//   — in the rank groups' order. A type that pairs with equipment the
//   homeowner keeps therefore shows both leads on the first view.
// - Revealed, under 'recommended': those lead cards first, then each type's
//   next-best systems grouped under it — the server interleaves the types by
//   rank so a page gives every type its top rows.
// - Revealed, under any other option: the lead cards first, then the loaded
//   rows exactly as the server ordered them, result set by result set (the
//   kept-constrained set before the alternatives).

export type DuctedRailPlaceholderItem = {
  kind: 'placeholder';
  key: string;
  status: 'loading' | 'error';
  systemType: SystemTypeFacetEntry;
};

export type DuctedRailCardItem = {
  kind: 'card';
  key: string;
  featured: boolean;
  resultSet: DuctedResultSet;
  selection: DuctedPanelSelection;
  productData: DuctedCombinationCardProps['productData'];
  accessoryData: DuctedCombinationCardProps['accessoryData'];
};

export type DuctedRailItem = DuctedRailPlaceholderItem | DuctedRailCardItem;

const featuredItem = (
  section: DuctedRailSectionModel,
  group: DuctedRailRankGroupModel,
  card: DuctedRailRankGroupCard
): DuctedRailItem | null => {
  const { representative, systemType } = card;
  const hasCombination = representative?.combination != null;
  if (representative?.status === 'loading' && !hasCombination) {
    return {
      kind: 'placeholder',
      key: group.key,
      status: 'loading',
      systemType
    };
  }
  if (representative?.status === 'error' && !hasCombination) {
    return { kind: 'placeholder', key: group.key, status: 'error', systemType };
  }
  const selection = rankGroupCardSelection(section, card);
  if (!selection || !representative) return null;
  return {
    kind: 'card',
    key: group.key,
    featured: true,
    resultSet: card.resultSet,
    selection,
    productData: representative.productData,
    accessoryData: representative.accessoryData
  };
};

const extraRepresentativeItems = (
  section: DuctedRailSectionModel,
  group: DuctedRailRankGroupModel
): DuctedRailCardItem[] => {
  const featured = featuredRankGroupCard(group);
  return group.cards.flatMap((card) => {
    const { representative } = card;
    if (card === featured || !representative?.combination) return [];
    const selection = rankGroupCardSelection(section, card);
    if (!selection) return [];
    return [
      {
        kind: 'card' as const,
        key: selection.cardId,
        featured: false,
        resultSet: card.resultSet,
        selection,
        productData: representative.productData,
        accessoryData: representative.accessoryData
      }
    ];
  });
};

const tailItems = (
  section: DuctedRailSectionModel,
  group: DuctedRailRankGroupModel
): DuctedRailCardItem[] =>
  rankGroupTailSelections(section, group).map(
    ({ selection, entry, resultSet }) => ({
      kind: 'card' as const,
      key: selection.cardId,
      featured: false,
      resultSet,
      selection,
      productData: entry.productData,
      accessoryData: entry.accessoryData
    })
  );

const tailPositionKey = (
  resultSet: DuctedResultSet,
  combination: Record<string, unknown>
): string => `${resultSet}:${String(combination.ID)}`;

// Where the server put every loaded tail row: result sets in section order,
// rows in response order within each.
const serverTailPositions = (
  section: DuctedRailSectionModel
): Map<string, [number, number]> => {
  const positions = new Map<string, [number, number]>();
  section.subSections.forEach((subSection, subSectionIndex) => {
    subSection.tail.forEach((combination, index) => {
      positions.set(tailPositionKey(subSection.resultSet, combination), [
        subSectionIndex,
        index
      ]);
    });
  });
  return positions;
};

const AFTER_EVERY_ROW: [number, number] = [
  Number.MAX_SAFE_INTEGER,
  Number.MAX_SAFE_INTEGER
];

const tailItemsInServerOrder = (
  section: DuctedRailSectionModel
): DuctedRailCardItem[] => {
  const positions = serverTailPositions(section);
  const positionOf = (item: DuctedRailCardItem): [number, number] =>
    positions.get(
      tailPositionKey(item.resultSet, item.selection.combination)
    ) ?? AFTER_EVERY_ROW;
  return section.rankGroups
    .flatMap((group) => tailItems(section, group))
    .sort((a, b) => {
      const [aSet, aRow] = positionOf(a);
      const [bSet, bRow] = positionOf(b);
      return aSet - bSet || aRow - bRow;
    });
};

// Whether the section holds anything "Show more" could reveal: a loaded
// row past each type's leads. Both result-set leads are already on the
// first view, so a second lead is not something left to reveal.
export const ductedSectionHasRowsBeyondLeads = (
  section: DuctedRailSectionModel
): boolean =>
  section.rankGroups.some((group) =>
    group.tails.some((tail) => tail.combinations.length > 0)
  );

// One type's leads, kept pairing before the full replacement. The first
// card keeps the rank-group anchor; the other result set's lead follows it.
const leadItems = (
  section: DuctedRailSectionModel,
  group: DuctedRailRankGroupModel
): DuctedRailItem[] => {
  const card = featuredRankGroupCard(group);
  const primary = card ? featuredItem(section, group, card) : null;
  return [
    ...(primary ? [primary] : []),
    ...extraRepresentativeItems(section, group)
  ];
};

export const buildDuctedSectionItems = (
  section: DuctedRailSectionModel,
  sort: SystemSortOption,
  tailRevealed: boolean
): DuctedRailItem[] => {
  const leads = section.rankGroups.flatMap((group) =>
    leadItems(section, group)
  );
  if (!tailRevealed) {
    return leads;
  }
  if (sort === 'recommended') {
    const rest = section.rankGroups.flatMap((group) =>
      tailItems(section, group)
    );
    return [...leads, ...rest];
  }
  return [...leads, ...tailItemsInServerOrder(section)];
};
